import { useEffect } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'
import { useAppStore } from './stores/appStore'
import { TabBar } from './components/TabBar'
import { DesktopShell } from './components/DesktopShell'
import { OfflineBanner } from './components/OfflineBanner'
import { useViewport } from './lib/useViewport'
import { AuthScreen } from './screens/AuthScreen'
import { OnboardingScreen } from './screens/OnboardingScreen'
import { FamilyBootstrapScreen } from './screens/FamilyBootstrapScreen'
import { TodayScreen } from './screens/TodayScreen'
import { TimelineScreen } from './screens/TimelineScreen'
import { NeedsListScreen } from './screens/NeedsListScreen'
import { NeedDetailScreen } from './screens/NeedDetailScreen'
import { NewNeedScreen } from './screens/NewNeedScreen'
import { FamilyScreen } from './screens/FamilyScreen'
import { AskScreen } from './screens/AskScreen'
import { SettingsScreen } from './screens/SettingsScreen'
import { PersonProfileScreen } from './screens/PersonProfileScreen'
import { FeedLoggerScreen } from './screens/FeedLoggerScreen'
import { DiaperLoggerScreen } from './screens/DiaperLoggerScreen'
import { SleepLoggerScreen } from './screens/SleepLoggerScreen'
import { MedicationLoggerScreen } from './screens/MedicationLoggerScreen'
import { NoteLoggerScreen } from './screens/NoteLoggerScreen'
import { GrowthLoggerScreen } from './screens/GrowthLoggerScreen'
import { LogScreen } from './screens/LogScreen'
import { DocumentsScreen } from './screens/DocumentsScreen'
import { WeeklyReportScreen } from './screens/WeeklyReportScreen'
import { HandoffScreen } from './screens/HandoffScreen'
import { JoinScreen } from './screens/JoinScreen'
import { SearchScreen } from './screens/SearchScreen'
import { TrendsScreen } from './screens/TrendsScreen'
import { OwnedItemsScreen } from './screens/OwnedItemsScreen'
import { OnboardingWizardScreen } from './screens/OnboardingWizardScreen'
import { CONSENT_VERSION, ConsentModal } from './screens/ConsentModal'
import { InstallPrompt } from './components/InstallPrompt'
import { ToastContainer } from './components/ToastContainer'
import { trackRouteChange } from './lib/analytics'

export default function App() {
  // All hooks MUST be declared before any conditional return per Rules of Hooks.
  const status = useAppStore((s) => s.status)
  const init = useAppStore((s) => s.init)
  const location = useLocation()
  const needsConsentReactive = useAppStore((s) => s.needsConsent)  // subscribe so consent modal shows up post-signin
  const viewport = useViewport()

  useEffect(() => {
    void init()
  }, [init])

  useEffect(() => {
    trackRouteChange(location.pathname)
  }, [location.pathname])

  // /join?token=... must render in EVERY auth state — clicking an invite link should always
  // land the recipient at the join flow, which then embeds AuthScreen if needed and accepts.
  const isJoinRoute = location.pathname === '/join'

  if (status === 'loading') return <LoadingSplash />
  if (status === 'unconfigured') return <UnconfiguredScreen />
  if (status === 'signed_out') {
    return isJoinRoute ? (
      <div className="mx-auto flex min-h-svh w-full max-w-lg flex-col">
        <Routes>
          <Route path="/join" element={<JoinScreen />} />
        </Routes>
      </div>
    ) : (
      <AuthScreen />
    )
  }

  // Signed in but no family yet — allow only Onboarding, Bootstrap, or Join routes.
  if (status === 'signed_in_no_family') {
    return (
      <div className="mx-auto flex min-h-svh w-full max-w-lg flex-col">
        <Routes>
          <Route path="/bootstrap" element={<FamilyBootstrapScreen />} />
          <Route path="/join" element={<JoinScreen />} />
          <Route path="*" element={<OnboardingScreen />} />
        </Routes>
      </div>
    )
  }

  const showTabs = !isLoggerRoute(location.pathname) && !isDetailRoute(location.pathname)
  const useDesktopShell = viewport === 'desktop'

  const routes = (
    <Routes>
      <Route path="/" element={<TodayScreen />} />
      <Route path="/bootstrap" element={<FamilyBootstrapScreen />} />
      <Route path="/needs" element={<NeedsListScreen />} />
      <Route path="/needs/new" element={<NewNeedScreen />} />
      <Route path="/needs/:id" element={<NeedDetailScreen />} />
      <Route path="/timeline" element={<TimelineScreen />} />
      <Route path="/timeline/event/:id" element={<TimelineScreen />} />
      <Route path="/family" element={<FamilyScreen />} />
      <Route path="/ask" element={<AskScreen />} />
      <Route path="/settings" element={<SettingsScreen />} />
      <Route path="/documents" element={<DocumentsScreen />} />
      <Route path="/reports/weekly" element={<WeeklyReportScreen />} />
      <Route path="/person/:id" element={<PersonProfileScreen />} />
      <Route path="/log" element={<LogScreen />} />
      <Route path="/log/feed" element={<FeedLoggerScreen />} />
      <Route path="/log/diaper" element={<DiaperLoggerScreen />} />
      <Route path="/log/sleep" element={<SleepLoggerScreen />} />
      <Route path="/log/medication" element={<MedicationLoggerScreen />} />
      <Route path="/log/note" element={<NoteLoggerScreen />} />
      <Route path="/log/growth" element={<GrowthLoggerScreen />} />
      <Route path="/handoff" element={<HandoffScreen />} />
      <Route path="/join" element={<JoinScreen />} />
      <Route path="/search" element={<SearchScreen />} />
      <Route path="/trends" element={<TrendsScreen />} />
      <Route path="/items" element={<OwnedItemsScreen />} />
      <Route path="/onboard" element={<OnboardingWizardScreen />} />
      <Route path="*" element={<NotFoundScreen />} />
    </Routes>
  )

  const overlays = (
    <>
      {needsConsentReactive && <ConsentModal onAccepted={() => useAppStore.getState().markConsented(CONSENT_VERSION)} />}
      <InstallPrompt />
      <ToastContainer />
    </>
  )

  if (useDesktopShell) {
    return (
      <DesktopShell>
        <OfflineBanner />
        <div className="flex-1">{routes}</div>
        {overlays}
      </DesktopShell>
    )
  }

  return (
    <div className="mx-auto flex min-h-svh w-full max-w-lg flex-col">
      <OfflineBanner />
      <main className="flex-1 pb-2">{routes}</main>
      {showTabs && <TabBar />}
      {overlays}
    </div>
  )
}

function isLoggerRoute(pathname: string): boolean {
  return pathname.startsWith('/log/') || pathname === '/handoff' || pathname === '/needs/new' || pathname === '/bootstrap' || pathname === '/join' || pathname.startsWith('/reports/')
}

function isDetailRoute(pathname: string): boolean {
  return /^\/(needs|person)\/[^/]+$/.test(pathname)
}

function LoadingSplash() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-bg-base">
      <div className="text-center text-text-tertiary">
        <div className="font-serif text-hero italic">Wren</div>
        <div className="mt-2 text-caption tracking-widest uppercase">Loading…</div>
      </div>
    </div>
  )
}

function UnconfiguredScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-bg-base px-6">
      <div className="max-w-sm text-center">
        <div className="font-serif italic text-hero text-text-primary">Wren</div>
        <div className="mt-4 text-body text-text-secondary">
          Configuration missing. Set <code className="font-mono text-pack-early-care">VITE_SUPABASE_URL</code> and{' '}
          <code className="font-mono text-pack-early-care">VITE_SUPABASE_ANON_KEY</code> in your build environment, then reload.
        </div>
      </div>
    </div>
  )
}

function NotFoundScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center bg-bg-base p-6 text-center">
      <div>
        <div className="font-serif italic text-hero">Not here.</div>
        <div className="mt-2 text-caption text-text-tertiary">The page you were looking for doesn't exist.</div>
      </div>
    </div>
  )
}
